/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { InventoryDashboardSnapshot } from '@/renderer/types/inventory';
import { AlertTriangle, Boxes, PackagePlus, Wallet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  formatCompactCurrency,
  formatCurrencyValue,
  getOverviewMetrics,
} from './inventoryDashboard';

interface InventoryDashboardKpiRowProps {
  snapshot: InventoryDashboardSnapshot;
}

const KPI_STYLES = [
  'bg-gradient-to-br from-pos-primary via-pos-secondary to-pos-primary text-white',
  'bg-white text-slate-900',
  'bg-white text-slate-900',
  'bg-white text-slate-900',
];

export function InventoryDashboardKpiRow({
  snapshot,
}: InventoryDashboardKpiRowProps) {
  const metrics = getOverviewMetrics(snapshot);
  const navigate = useNavigate();
  const items = [
    {
      key: 'stock_value',
      label: 'Total stock value',
      value: metrics.totalStockValue,
      caption: `${metrics.activeItems.toLocaleString()} active items`,
      icon: Wallet,
      emphasize: true,
    },
    {
      key: 'low_stock',
      label: 'Low-stock items',
      value: metrics.lowStockItems,
      caption: `${metrics.stockLocations.toLocaleString()} stock locations monitored`,
      icon: AlertTriangle,
      emphasize: false,
      suffix: '',
    },
    {
      key: 'purchases',
      label: 'Purchases in range',
      value: metrics.purchaseValue,
      caption: `${metrics.purchaseInvoiceCount.toLocaleString()} invoices in this range`,
      icon: PackagePlus,
      emphasize: false,
    },
    {
      key: 'payables',
      label: 'Outstanding payables',
      value: metrics.outstandingPayables,
      caption: `${formatCompactCurrency(metrics.amountPaidInRange)} paid in this range`,
      icon: Boxes,
      emphasize: false,
    },
  ];

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      {items.map((item, index) => {
        const Icon = item.icon;
        const valueLabel =
          typeof item.value === 'number' && item.key === 'low_stock'
            ? item.value.toLocaleString()
            : formatCompactCurrency(item.value);

        const isClickable = item.key === 'low_stock';

        return (
          <div
            key={item.key}
            onClick={
              isClickable
                ? () => navigate('/inventory/stock/low-stock')
                : undefined
            }
            className={`rounded-[20px] border border-gray-200 p-3.5 shadow-sm ${KPI_STYLES[index]} ${isClickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p
                  className={`text-xs font-medium ${
                    item.emphasize ? 'text-white/80' : 'text-slate-500'
                  }`}
                >
                  {item.label}
                </p>
                <p className="mt-1.5 text-[1.75rem] font-semibold tracking-tight">
                  {valueLabel}
                </p>
                <p
                  className={`mt-1.5 text-[11px] ${
                    item.emphasize ? 'text-white/75' : 'text-slate-500'
                  }`}
                >
                  {item.caption}
                </p>
                {item.key !== 'low_stock' ? (
                  <p
                    className={`mt-0.5 text-[10px] ${
                      item.emphasize ? 'text-white/65' : 'text-slate-400'
                    }`}
                  >
                    {formatCurrencyValue(item.value)}
                  </p>
                ) : null}
              </div>
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                  item.emphasize
                    ? 'bg-white/15 text-white'
                    : 'bg-pos-primary/10 text-pos-primary'
                }`}
              >
                <Icon size={18} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
